import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import KubitModal from '../../../../KubitModal';
import { PrimaryButton, SecondaryButton } from '../../../../Button';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  CREATE_VIRTUAL_EVENT,
  UPDATE_VIRTUAL_EVENT,
} from '../../../../../utils/apollo/virtualEvents';
import { GET_VIRTUAL_EVENTS } from '../../../../../pages/Formulas/apollo';
import {
  IRowMeta,
  replaceVirtualEvent,
} from '../../../../../redux/slices/oldBuilder';
import {
  ICreateVirtualEventMutation,
  ICreateVirtualEventMutationVariables,
  IEventConditionFieldsFragment,
  IGetVirtualEventsQuery,
  IGetVirtualEventsQueryVariables,
  IManageVersionEntityMutation,
  IManageVersionEntityMutationVariables,
  IUpdateVirtualEventMutation,
  IUpdateVirtualEventMutationVariables,
  IVirtualEventFieldsFragment,
  ManagedEntityType,
  PermissionKey,
} from '../../../../../utils/apollo/graphqlSchema';
import BuilderVirtualEventDropdown from './BuilderVirtualEventDropdown';
import RadioGroup from '../../../../RadioGroup';
import { useStrategyOptions } from '../../../../../utils/hooks';
import * as FormComponents from '../../../../Form';
import { CommentField, DescriptionField, NameField } from '../../../../Form';
import printGraphqlErrors from '../../../../../utils/apollo/printGraphqlErrors';
import { useHighlightingContext } from '../../../../HighlightingContext';
import { ModalActions } from '../../../../KubitModal/KubitModalUI';
import SaveEntityHeader, {
  SaveEntityHeaderEnum,
} from '../../../../SaveEntityHeader';
import usePermission from '../../../../../utils/hooks/permissionHooks/usePermission';
import PermissionElementWrapper from '../../../../../pages/PermissionControls/PermissionElementWrapper';
import {
  DESCRIPTION_MAX_LENGTH,
  EVENT_NAME_MAX_LENGTH,
} from '../../../../../config/constants';
import { SEARCH_TAGS } from '../../../../../utils/apollo/entitySelection/query';
import { parseEventConditionToInput } from '../../../utils/builderUtils';
import { ApplyTags } from '../../../../Categories';
import { useSaveCategories } from '../../../../../utils/categories';
import toastMessage from '../../../../../utils/toastMessage';
import { MANAGE_VERSION_ENTITY } from '../../../../../utils/apollo/manageView/mutations';
import {
  assertNotNil,
  ICreatableReactSelectOption,
} from '../../../../../utils/types';
import { defaultManagedOption } from '../../../../Categories/utils';
import { useAnalytics } from '../../../../../utils/analytics/useAnalytics';
import { UnreachableCaseError } from 'ts-essentials';
import { virtualEventSaved } from '../../../../../utils/analytics/events/event/events';
import useQuickAccessDictionaryViewState from '../../../../../redux/selectorHooks/useQuickAccessDictionaryViewState';
import ThinDivider from '../../../../ThinDivider';

interface IProps {
  open: boolean;
  activeSchemaId: string;
  onClose: () => void;
  onSubmit: () => void;
  eventConditions: IEventConditionFieldsFragment[];
  rowMeta: IRowMeta;
}

const CommentFormGroup = styled(CommentField)`
  margin-top: 24px;
`;

const StrategyOptions = styled(RadioGroup)`
  margin-bottom: 30px;
`;
const TagsContainer = styled.div`
  margin-bottom: 24px;
`;

export default ({
  open,
  onClose,
  onSubmit,
  activeSchemaId,
  eventConditions,
  rowMeta,
}: IProps) => {
  const dispatch = useDispatch();
  const client = useApolloClient();
  const { track } = useAnalytics();

  const { checkIsPermitted } = usePermission();
  const isPermittedToCreateVirtualEvent = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.VirtualEventCreate],
    schemaId: activeSchemaId,
  });
  const isPermittedToReadVirtualEvent = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.VirtualEventRead],
    schemaId: activeSchemaId,
  });
  const isPermittedToUpdateVirtualEvent = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.VirtualEventEdit],
    schemaId: activeSchemaId,
  });
  const { strategy, setStrategy, strategyOptions } = useStrategyOptions({
    defaultStrategy: isPermittedToCreateVirtualEvent ? 'create' : 'update',
    permittedStatus: {
      create: isPermittedToCreateVirtualEvent,
      update: isPermittedToUpdateVirtualEvent,
    },
  });
  const isDictionarySidebarExpanded = useQuickAccessDictionaryViewState();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [comment, setComment] = useState('');
  const [searchText, setSearchText] = useState('');
  const [virtualEvents, setVirtualEvents] = useState<
    IVirtualEventFieldsFragment[]
  >([]);
  const [selectedId, setSelectedId] = useState<string>('');

  const { activateHighlighting, clearHighlightedByTimeout } =
    useHighlightingContext();

  const onFetchVirtualEvents = (res: IGetVirtualEventsQuery) => {
    const entities = res.virtualEvents?.entities;
    if (entities) {
      setVirtualEvents(entities);
    }
  };

  const skipVirtualEventsQuery =
    strategy === 'create' ||
    !isPermittedToReadVirtualEvent ||
    !isPermittedToUpdateVirtualEvent;
  const { loading } = useQuery<
    IGetVirtualEventsQuery,
    IGetVirtualEventsQueryVariables
  >(GET_VIRTUAL_EVENTS, {
    variables: {
      schemaId: activeSchemaId,
      term: searchText,
      includeIds: [],
      pagination: {
        first: 50,
        after: 0,
      },
    },
    onCompleted: onFetchVirtualEvents,
    skip: skipVirtualEventsQuery,
  });

  const [createVirtualEvent, { loading: virtualEventIsCreating }] = useMutation<
    ICreateVirtualEventMutation,
    ICreateVirtualEventMutationVariables
  >(CREATE_VIRTUAL_EVENT);

  const [updateVirtualEvent, { loading: virtualEventIsUpdating }] = useMutation<
    IUpdateVirtualEventMutation,
    IUpdateVirtualEventMutationVariables
  >(UPDATE_VIRTUAL_EVENT);

  const [manageVersionedEntity] = useMutation<
    IManageVersionEntityMutation,
    IManageVersionEntityMutationVariables
  >(MANAGE_VERSION_ENTITY);

  const {
    categories,
    setCategories,
    prepareCategories,
    isEntityManaged,
    setIsEntityManaged,
  } = useSaveCategories(activeSchemaId);

  const onSelectNameChange = (selectedOptions: any, _: boolean) => {
    if (skipVirtualEventsQuery) {
      return [];
    }
    if (selectedOptions.length === 0) {
      setSelectedId('');
      setCategories([]);
      return;
    }
    const eventId = selectedOptions[0].id;
    setSelectedId(eventId);
    const virtualEvent = virtualEvents.find((ve) => ve.id === eventId);
    setDescription(virtualEvent?.description ?? '');
    setCategories(
      virtualEvent?.categories?.map((cat) => ({
        label: cat.name,
        value: cat.id,
        ...cat,
      })) ?? [],
    );

    if (virtualEvent?.isManaged) {
      setIsEntityManaged(true);
    }

    return;
  };

  const handleChangeStrategy = (selectedStrategy: string) => {
    setStrategy(selectedStrategy);

    if (name) {
      setName('');
    }

    if (selectedId) {
      setSelectedId('');
    }

    if (description) {
      setDescription('');
    }

    if (categories.length > 0) {
      setCategories([]);
    }
  };

  const handleSubmit = async () => {
    // prepare eventConditionInputs for create and update mutations
    const eventConditionInputs = eventConditions.map((eventCondition) =>
      parseEventConditionToInput(eventCondition, activeSchemaId, false),
    );

    const inputCategories = await prepareCategories();

    let respond;
    let successToastMessage;
    let errors;
    let entity;
    if (strategy === 'create') {
      respond = await createVirtualEvent({
        variables: {
          input: {
            description,
            comment,
            eventConditions: eventConditionInputs,
            name,
            schema: activeSchemaId,
            categories: inputCategories,
          },
        },
      });
      successToastMessage = `${name} has been created.`;
      errors = respond.data?.virtualEventCreate.errors;
      entity = respond.data?.virtualEventCreate.entity;
    } else if (strategy === 'update') {
      // name is id here, therefore search for id in entities
      respond = await updateVirtualEvent({
        variables: {
          input: {
            name: null,
            description,
            comment,
            eventConditions: eventConditionInputs,
            virtualEventId: selectedId,
            categories: inputCategories,
          },
        },
      });
      const virtualEvent = virtualEvents.find((ve) => ve.id === selectedId);
      successToastMessage = `${virtualEvent?.name} has been updated.`;
      errors = respond.data?.virtualEventEdit.errors;
      entity = respond.data?.virtualEventEdit.entity;
    } else {
      throw new UnreachableCaseError(strategy);
    }
    // check errors and show toast
    if (printGraphqlErrors(errors)) {
      return;
    } else {
      toastMessage({ message: successToastMessage, type: 'info' });
    }
    assertNotNil(entity);

    if (isEntityManaged) {
      const virtualEventId = entity.id;

      const res = await manageVersionedEntity({
        variables: {
          input: {
            entityId: virtualEventId,
            entityType: ManagedEntityType.VirtualEvent,
            isManaged: true,
          },
        },
      });

      if (res.data?.manageVersionEntity?.errors) {
        printGraphqlErrors(res.data.manageVersionEntity.errors);
      }
    }

    // replace basic event rows into virtual event to avoid re-fetching
    dispatch(replaceVirtualEvent({ virtualEvent: entity, rowMeta }));

    activateHighlighting();

    clearHighlightedByTimeout();

    // refetch latest virtual events for dictionary sidebar
    if (isDictionarySidebarExpanded) {
      client.refetchQueries({
        include: [SEARCH_TAGS],
      });
    }

    track(virtualEventSaved(entity));
    onSubmit();
  };

  const handleChangeTags = (values: ICreatableReactSelectOption[]) => {
    const managed = values.find(
      (value) => value.value === defaultManagedOption.value,
    );
    setIsEntityManaged(!!managed);
    setCategories(
      values.filter((value) => value.value !== defaultManagedOption.value),
    );
  };

  const selectedVirtualEvent = virtualEvents.find((ve) => ve.id === selectedId);

  const isSaveDisabled =
    loading ||
    (!name.length && !selectedId) ||
    name.length > EVENT_NAME_MAX_LENGTH ||
    virtualEventIsCreating ||
    virtualEventIsUpdating ||
    comment.length > DESCRIPTION_MAX_LENGTH ||
    description.length > DESCRIPTION_MAX_LENGTH;

  return (
    <KubitModal open={open} onClose={onClose} header="Save Virtual Event">
      <SaveEntityHeader entityType={SaveEntityHeaderEnum.VirtualEvent} />
      <StrategyOptions
        name="strategy"
        value={strategy}
        onChange={handleChangeStrategy}
        options={strategyOptions}
      />
      {strategy === 'create' ? (
        <NameField
          label={'Name'}
          value={name}
          onChange={setName}
          placeholder="Enter new event name"
          targetErrorElement="Virtual event"
          maxLength={EVENT_NAME_MAX_LENGTH}
          disabled={loading || virtualEventIsCreating}
        />
      ) : (
        <FormComponents.VerticalField>
          <FormComponents.FieldLabel>Name</FormComponents.FieldLabel>
          <PermissionElementWrapper isPermitted={isPermittedToReadVirtualEvent}>
            <BuilderVirtualEventDropdown
              virtualEventOptions={virtualEvents}
              selected={selectedVirtualEvent ? [selectedVirtualEvent] : []}
              onChange={onSelectNameChange}
              searchText={searchText}
              onSearchChange={setSearchText}
              isLoading={loading}
              blurInputOnSelect
              isDisabled={!isPermittedToReadVirtualEvent}
            />
          </PermissionElementWrapper>
        </FormComponents.VerticalField>
      )}
      <DescriptionField
        label={'Description'}
        value={description}
        onChange={setDescription}
        placeholder="Add event description here"
        targetErrorElement="Virtual event"
        disabled={loading || virtualEventIsCreating || virtualEventIsUpdating}
      />
      <TagsContainer>
        <ApplyTags
          schemaId={activeSchemaId}
          selectedValues={categories}
          onChange={handleChangeTags}
          isManaged={isEntityManaged}
        />
      </TagsContainer>
      <ThinDivider layout="horizontal" size="full" />
      <CommentFormGroup
        label={'Comment'}
        value={comment}
        onChange={setComment}
        placeholder="Add your comment here"
        targetErrorElement="Virtual event"
        disabled={loading || virtualEventIsCreating || virtualEventIsUpdating}
      />
      <ModalActions>
        <SecondaryButton
          type="button"
          onClick={onClose}
          disabled={virtualEventIsCreating || virtualEventIsUpdating}
          trackContext="virtial event"
          trackAction="cancel"
        >
          Cancel
        </SecondaryButton>
        <PrimaryButton
          type="button"
          onClick={handleSubmit}
          disabled={isSaveDisabled}
          trackContext="virtial event"
          trackAction="save"
        >
          Save
        </PrimaryButton>
      </ModalActions>
    </KubitModal>
  );
};
